import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import { getImageUrlHelper, truncateText } from "../../helper/helper";

import {
  getCompanyProducts,
  createCompanyProduct,
  updateCompanyProduct,
  deleteCompanyProduct,
} from "../../redux/companyProduct/companyProductSlice";
import PaginationHelper from '../../helper/PaginationHelper';

const initialState = {
  name: "",
  description: "",
  link: "",
  file: null,
};

const CompanyProductLayer = () => {
  const dispatch = useDispatch();

  const { products = [], total = 0 } = useSelector(
    (state) => state.products || {}
  );

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState(initialState);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    dispatch(
      getCompanyProducts({
        page: currentPage,
        limit: entries,
        search,
      })
    );
  }, [dispatch, currentPage, entries, search]);

  const totalPages = Math.ceil(total / entries);

  const startIndex = (currentPage - 1) * entries;

  const openCreate = () => {
    setFormData(initialState);
    setImagePreview(null);
    setIsEdit(false);
  };

  const openEdit = (p) => {
    setFormData({
      name: p.name || "",
      description: p.description || "",
      link: p.link || "",
      file: null,
    });

    setImagePreview(getImageUrlHelper(p.image || p.file));

    setEditId(p.id);
    setIsEdit(true);
  };

  const handleSubmit = () => {

    if (!formData.name || !formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (formData.name.length > 100) {
      toast.error("Name max 100 characters");
      return;
    }

    if (formData.description && formData.description.length > 255) {
      toast.error("Description max 255 characters");
      return;
    }

    if (formData.link) {
      const urlPattern = /^(https?:\/\/)[^\s$.?#].[^\s]*$/;
      if (!urlPattern.test(formData.link)) {
        toast.error("Enter valid URL (must start with http/https)");
        return;
      }
    }

    if (!isEdit && !formData.file) {
      toast.error("Image is required");
      return;
    }

    const fd = new FormData();
    fd.append("name", formData.name.trim());
    fd.append("description", formData.description || "");
    fd.append("link", formData.link || "");

    if (formData.file) fd.append("file", formData.file);

    const action = isEdit
      ? updateCompanyProduct({ id: editId, data: fd })
      : createCompanyProduct(fd);

    dispatch(action)
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Success");

        dispatch(
          getCompanyProducts({
            page: currentPage,
            limit: entries,
            search,
          })
        );

        setFormData(initialState);
        setImagePreview(null);
        setEditId(null);

        document.querySelector("#productModal .btn-close")?.click();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Error"
        );
      });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }

    setFormData({ ...formData, file });
    setImagePreview(URL.createObjectURL(file));
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        {/* HEADER (MATCHED UI) */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light">
              Show
            </span>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={entries}
              onChange={(e) => {
                setEntries(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>

            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#productModal"
            onClick={openCreate}
          >
            <Icon icon="ic:baseline-plus" />
            Add Product
          </button>
        </div>

        {/* TABLE */}
        <div className="card-body p-12">
          <div className="table-responsive">
            <table className="table bordered-table sm-table">
              <thead>
                <tr>
                  <th>S.L</th>
                  <th>Image</th>
                  <th>Name</th>
                  {/* <th>Description</th> */}
                  {/* <th>Link</th> */}
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {products.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No data found
                    </td>
                  </tr>
                )}

                {products.map((p, index) => (
                  <tr key={p.id}>
                    <td>{startIndex + index + 1}</td>

                    <td>
                      <img
                        src={getImageUrlHelper(p.image)}
                        className="w-40-px h-40-px object-fit-cover radius-8"
                        alt="product"
                      />
                    </td>

                    <td>{truncateText(p.name, 30)}</td>
                    {/* <td>{truncateText(p.description, 40)}</td> */}
                    {/* <td>{truncateText(p.link || "-", 30)}</td> */}

                    <td className="text-center">
                      <div className="d-flex gap-10 justify-content-center">
                        <button
                          className="bg-success-focus text-success-600 bg-hover-success-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          data-bs-toggle="modal"
                          data-bs-target="#productModal"
                          onClick={() => openEdit(p)}
                        >
                          <Icon icon="lucide:edit" />
                        </button>

                        <button
                          className="bg-danger-focus bg-hover-danger-200 text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          data-bs-toggle="modal"
                          data-bs-target="#deleteModal"
                          onClick={() => setDeleteId(p.id)}
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <PaginationHelper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={total}
            pageSize={entries}
          />
        </div>
      </div>

      {/* MODAL */}
      <div className="modal fade" id="productModal">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16">
            <div className="modal-header">
              <h5>{isEdit ? "Update Product" : "Add Product"}</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Name <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                {/* <div className="col-md-6 mb-3">
                  <label>
                    Link <small className="fst-italic">(optional)</small>
                  </label>

                  <input
                    className="form-control"
                    placeholder="https://example.com"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                  />
                </div> */}
                {/* <div className="col-md-6 mb-3">
                  <label>
                    Description <small className="fst-italic">(max 255 chars)</small>
                  </label>

                  <textarea
                    className="form-control"
                    rows={4}
                    maxLength={255}
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                  />
                </div> */}

                <div className="col-md-6 mb-3">
                  <label>
                    Upload Image <span className="text-danger">*</span>
                  </label>

                  <label
                    className="upload-file border radius-8 border-dashed d-flex align-items-center justify-content-center"
                    style={{ height: "150px", cursor: "pointer" }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        className="w-100 h-100 object-fit-cover radius-8"
                      />
                    ) : (
                      <div className="text-center">
                        <Icon icon="solar:camera-outline" width="24" />
                        <p className="mb-0">Upload Image <small className="fst-italic">(1:1 - 100x100 )</small></p>
                      </div>
                    )}

                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>

              <div className="text-end">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  {isEdit ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DELETE */}
      <div className="modal fade" id="deleteModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 text-center p-4">
            <p>Are you sure?</p>

            <button
              className="btn btn-danger"
              onClick={() => {
                dispatch(deleteCompanyProduct(deleteId))
                  .unwrap()
                  .then(() => {
                    toast.success("Deleted");
                    dispatch(
                      getCompanyProducts({
                        page: currentPage,
                        limit: entries,
                        search,
                      })
                    );
                    document.querySelector("#deleteModal .btn-close")?.click();
                  });
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyProductLayer;